import * as React from "react";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Autocomplete from "@mui/material/Autocomplete";
import { useState, useEffect } from "react";
import isAuthenticated from "../../components/Utils/isAuthenticated";
import { useFacultyRank } from "../../Hooks/useFacultyRankQuery";
import { getUserAuthLevels, getWebUsers } from "../Api/Getters";
import { useNavigate } from "react-router-dom";

export default function InstructorSearchAutocomplete(props) {
	const auth = isAuthenticated();
	const token = auth.token;
	const { data: facultyRank } = useFacultyRank(token);

	const [options, setOptions] = React.useState([]);
	const [value, setValue] = React.useState(null);
	const [openSearch, setOpenSearch] = useState(false);
	const navigate = useNavigate();

	useEffect(() => {
		const buildOptionsFromFacultyRank = () => {
			if (facultyRank && facultyRank.length > 0) {
				return facultyRank.map((element, index) => ({
					key: `faculty-${index}`, // prefix to keep keys unique
					empid: element.empid,
					name: element.name,
					ccid: element.ccid,
					rank: element.rank,
				}));
			}
			return [];
		};

		const fetchWebUsersAndMerge = async () => {
			const facultyOptions = buildOptionsFromFacultyRank();
			const apiData = await getWebUsers(token);
			const webUserOptions = (apiData ?? []).map((e) => ({
				empid: e.empid,
				name: e.name,
				ccid: e.ccid,
				rank: e.rank,
			}));

			const byId = new Map();

			// helper to choose a stable key; prefer ccid, fall back to empid
			const idFor = (x) => x.ccid ?? `empid:${x.empid}`;

			// First seed with faculty
			for (const f of facultyOptions) {
				byId.set(idFor(f), { ...f, sources: ["faculty"] });
			}

			// Then merge in web users (web wins for selected fields; adjust as needed)
			for (const w of webUserOptions) {
				const k = idFor(w);
				const existing = byId.get(k);
				if (!existing) {
					byId.set(k, { ...w, sources: ["web"] });
				} else {
					byId.set(k, {
						...existing,
						// choose precedence per field:
						empid: existing.empid ?? w.empid,
						ccid: existing.ccid ?? w.ccid,
						name: w.name ?? existing.name, // prefer web name if present
						rank: w.rank ?? existing.rank, // prefer web rank if present
						sources: Array.from(new Set([...(existing.sources || []), "web"])),
					});
				}
			}

			setOptions(Array.from(byId.values()));
		};

		fetchWebUsersAndMerge();
	}, [facultyRank, token, props.currentRow]);

	const handleSubmit = () => {
		if (value === null) {
			alert("Error impersonating instructor. Please try again.");
		} else {
			if (value.empid === null || value.ccid === null || value.name === null) {
				alert("Error impersonating instructor. Please try again.");
			} else {
				if (!sessionStorage.getItem("original_ccid")) {
					sessionStorage.setItem("original_ccid", sessionStorage.getItem("ccid") ?? "");
				}
				if (!sessionStorage.getItem("original_empid")) {
					sessionStorage.setItem("original_empid", sessionStorage.getItem("id") ?? "");
				}
				sessionStorage.setItem("id", value.empid);
				sessionStorage.setItem("ccid", value.ccid);
				sessionStorage.setItem("name", value.name);
				setTimeout(() => {
					window.location.reload();
				}, 1000);
			}
		}
	};

	const handleSubmitFull = async () => {
		const apiData = await getUserAuthLevels(token, value.ccid);

		if (apiData) {
			if (!sessionStorage.getItem("original_ccid")) {
				sessionStorage.setItem("original_ccid", sessionStorage.getItem("ccid") ?? "");
			}
			if (!sessionStorage.getItem("original_empid")) {
				sessionStorage.setItem("original_empid", sessionStorage.getItem("id") ?? "");
			}
			if (!sessionStorage.getItem("original_access")) {
				sessionStorage.setItem("original_access", sessionStorage.getItem("access") ?? "");
			}
			sessionStorage.setItem("access", apiData.access);
			sessionStorage.setItem("viewable_empids", apiData.empids);
			sessionStorage.setItem("dept_ids", apiData.dept_ids);
			sessionStorage.setItem("user_settings", JSON.stringify(apiData.user_settings));
			sessionStorage.setItem("id", value.empid);
			sessionStorage.setItem("ccid", value.ccid);
			sessionStorage.setItem("name", value.name);
			sessionStorage.setItem("first_name", value.name.split(" ")[1]);
			//remove commas from last name
			sessionStorage.setItem("last_name", value.name.split(" ")[0].replace(/,/g, ""));

			setTimeout(() => {
				navigate(0);
			}, 1000);
		}
	};

	return (
		<React.Fragment>
			<div className="flex flex-col items-center text-center w-full gap-1">
				<Autocomplete
					onClick={props.onClick}
					value={value}
					onChange={(event, newValue) => {
						event.stopPropagation();
						if (typeof newValue === "string") {
							// timeout to avoid instant validation of the dialog's form.
							setTimeout(() => {
								setOpenSearch(false);
							});
						} else if (newValue && newValue.inputValue) {
							setOpenSearch(false);
						} else {
							setValue(newValue);
							setOpenSearch(false);
						}
					}}
					onInputChange={(event, newInputValue) => {
						setValue(null);

						setOpenSearch(newInputValue.length > 2);
					}}
					id="free-solo-dialog-demo"
					options={options}
					getOptionLabel={(option) => {
						if (typeof option === "string") {
							return option;
						}
						if (option.inputValue) {
							return option.inputValue;
						}
						return option.name + " - " + option.empid + " - " + option.ccid;
					}}
					filterOptions={(options, { inputValue }) => {
						const trimmedInput = inputValue.trim();

						if (trimmedInput.includes(" ")) {
							const tokens = trimmedInput.split(/\s+/).map((token) => token.toLowerCase());

							return options.filter((option) => {
								const parts = [
									option.name?.toLowerCase() || "",
									option.empid?.toLowerCase() || "",
									option.ccid?.toLowerCase() || "",
								];
								return tokens.every((token) => parts.some((part) => part.includes(token)));
							});
						} else {
							const normalizedInput = trimmedInput.replace(/,/g, "").toLowerCase();

							return options.filter((option) => {
								const searchableString = `${option.name || ""} ${option.empid || ""} ${
									option.ccid || ""
								}`
									.replace(/,/g, "")
									.toLowerCase();

								return searchableString.includes(normalizedInput);
							});
						}
					}}
					disableClearable
					filterSelectedOptions
					selectOnFocus
					clearOnBlur
					renderOption={(props, option) => {
						const { key, ...otherProps } = props;
						return (
							<li key={key} {...otherProps}>
								{option.name ? option.name : ""} - {option.empid ? option.empid : "WEB"} -{" "}
								{option.ccid ? option.ccid : ""}
							</li>
						);
					}}
					sx={{ width: "100%" }}
					freeSolo
					open={openSearch}
					renderInput={(params) => <TextField {...params} label="Employee ID or CCID" />}
				/>
				<div className="flex gap-2  w-full">
					<button
						disabled={!value}
						variant="contained"
						className="bg-primary flex flex-col text-white px-4 py-2 w-full disabled:bg-gray-200 disabled:text-gray-400 rounded disabled:pointer-events-none hover:cursor-pointer hover:bg-primary/90"
						onClick={handleSubmit}
					>
						Superficial<span className="text-xs text-gray-300">CCID, EMPID</span>
					</button>
					<button
						className="bg-primary flex flex-col text-white px-4 py-2 w-full disabled:bg-gray-200 disabled:text-gray-400 rounded disabled:pointer-events-none hover:cursor-pointer hover:bg-primary/90"
						disabled={!value}
						variant="contained"
						onClick={handleSubmitFull}
					>
						Full<span className="text-xs text-gray-300">ACCESS, AUTH</span>
					</button>
				</div>
			</div>
		</React.Fragment>
	);
}
